import { FilterBar, FilterController, FilterList, FilterSelection } from "@amzn/stencil-react-components/filtering";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { IconSortExtraSmall } from "@amzn/stencil-react-icons";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC } from "react";

import { getSortConfigs } from "./sortingConfig";
import { useDefaultSortingDetection } from "./useDefaultSortingDetection";
import { useSortingLogic } from "./useSortingLogic";

const STRING_PATH = "searchResults.sorting";

export interface SearchSortingOptionsProps {
  priceSort: FilterSelection | undefined;
  alphabeticSort: FilterSelection | undefined;
  bestMatchSort: FilterSelection | undefined;
  setPriceSort: (value: FilterSelection) => void;
  setAlphabeticSort: (value: FilterSelection) => void;
  setBestMatchSort: (value: FilterSelection) => void;
  ratingsSort?: FilterSelection | undefined;
  setRatingsSort?: (value: FilterSelection) => void;
  viewsSort?: FilterSelection | undefined;
  setViewsSort?: (value: FilterSelection) => void;
  searchQuery: string;
  showBestMatch: boolean;
  isBestMatchActive: boolean;
  onClearSorting: () => void;
}

export const SearchSortingOptions: FC<SearchSortingOptionsProps> = ({
  priceSort,
  alphabeticSort,
  bestMatchSort,
  setPriceSort,
  setAlphabeticSort,
  setBestMatchSort,
  ratingsSort,
  setRatingsSort,
  viewsSort,
  setViewsSort,
  searchQuery,
  showBestMatch,
  isBestMatchActive,
  onClearSorting,
}) => {
  // Get sorting configuration
  const sortConfigs = getSortConfigs();

  // Detect if current sorting is at default state
  const { isAtDefaultSorting } = useDefaultSortingDetection({
    searchQuery,
    isBestMatchActive,
    bestMatchSort,
    priceSort,
    alphabeticSort,
  });

  // Get sorting logic handlers
  const { sortState, handleFilterChange, handleApply } = useSortingLogic({
    bestMatchSort,
    setBestMatchSort,
    priceSort,
    setPriceSort,
    alphabeticSort,
    setAlphabeticSort,
    ratingsSort,
    setRatingsSort,
    viewsSort,
    setViewsSort,
    sortConfigs,
    onClearSorting,
  });

  return (
    <Row
      dataTestId="search-sorting-options"
      alignItems="center"
      gridGap={"S200"}
      margin={["S300", "0", "0", "0"]}
      width={"100%"}
    >
      <>
        <IconSortExtraSmall color="black !important" />
        <Text color="black">{getString(`${STRING_PATH}.sortBy`)}</Text>
      </>
      <Col margin={["S100", "0", "0", "0"]} height={"100%"} width={"90%"}>
        <FilterBar
          flyoutTitleText={getString(`${STRING_PATH}.titleText`)}
          onApply={handleApply}
          resetButtonText={isAtDefaultSorting ? "" : getString(`${STRING_PATH}.clearAll`)}
          shouldShowAppliedFiltersRow={false}
          dataTestId="sorting-filter-bar"
        >
          {sortConfigs.map((config) => {
            const { key, titlePath, clearPath, options, isRequired } = config;
            const sortValue = sortState[key as keyof typeof sortState].value;

            // Skip bestMatch if not shown
            if (key === "bestMatch" && !showBestMatch) {
              return null;
            }

            return (
              <FilterController key={key} name={key} titleText={getString(titlePath)} dataTestId={`search-${key}-sort`}>
                <FilterList
                  onFilterChange={handleFilterChange(key)}
                  options={options}
                  titleText={getString(titlePath)}
                  value={sortValue}
                  resetButtonText={getString(clearPath)}
                  isRequired={isRequired}
                />
              </FilterController>
            );
          })}
        </FilterBar>
      </Col>
    </Row>
  );
};

export default SearchSortingOptions;
